/**
 * Data hooks. Reads are React Query queries keyed by resource; writes go
 * through `useWrite`, which invalidates everything that could show the change
 * (the hub is small enough that broad invalidation is cheaper than being clever).
 */
import { useMutation, useQuery, useQueryClient, keepPreviousData, type QueryKey } from "@tanstack/react-query";
import type {
  MailStatus,
  RetentionItem,
  AccountsOverview,
  Asset,
  AudiencePreview,
  CalendarItem,
  DataRequest,
  ExportDatasetInfo,
  ExportFilterValues,
  ExportPreview,
  GoogleCalendarStatus,
  KeyResultDetail,
  PlannedActivity,
  ProposalPreview,
  BookingSlot,
  BookingStatus,
  FormSubmission,
  WebForm,
  ProposalSummary,
  ProposalTemplateInfo,
  EmailAudience,
  CampaignDetail,
  CampaignSummary,
  Dashboard,
  Deliverability,
  EmailDelivery,
  EmailSendDetail,
  EmailSendSummary,
  EmailTemplate,
  InsightDatasetSchema,
  InsightQuery,
  InsightResult,
  MergeField,
  OkrOverview,
  PartnerDetail,
  PartnerListItem,
  PublicPreferences,
  ReferralItem,
  AuditEntry,
  AuthConfig,
  ConfigBundle,
  Contact,
  ContactRegistryItem,
  DealDetail,
  DealSummary,
  HomeData,
  Me,
  OrganisationDetail,
  OrganisationListItem,
  Paged,
  SearchResults,
  TimelineItem,
  TrackedLinkDetail,
  TrackedLinksOverview,
} from "@otii/shared";
import { toast } from "sonner";
import { api, ApiError, get, post, qs } from "./api";

export const keys = {
  me: ["me"] as const,
  authConfig: ["auth-config"] as const,
  config: ["config"] as const,
  home: ["home"] as const,
  organisations: (p: Record<string, unknown>) => ["organisations", p] as const,
  organisation: (id: string) => ["organisation", id] as const,
  timeline: (id: string, kinds?: string) => ["timeline", id, kinds ?? "all"] as const,
  contacts: (p: Record<string, unknown>) => ["contacts", p] as const,
  contact: (id: string) => ["contact", id] as const,
  deals: (p: Record<string, unknown>) => ["deals", p] as const,
  deal: (id: string) => ["deal", id] as const,
  accounts: ["accounts"] as const,
  search: (q: string) => ["search", q] as const,
  audit: (p: Record<string, unknown>) => ["audit", p] as const,
  partners: (p: Record<string, unknown>) => ["partners", p] as const,
  partner: (id: string) => ["partner", id] as const,
  referrals: (p: Record<string, unknown>) => ["referrals", p] as const,
  okr: ["okr"] as const,
  campaigns: ["campaigns"] as const,
  campaign: (id: string) => ["campaign", id] as const,
  assets: (p: Record<string, unknown>) => ["assets", p] as const,
  emailTemplates: ["email-templates"] as const,
  emailTemplate: (id: string) => ["email-template", id] as const,
  emailSends: ["email-sends"] as const,
  emailSend: (id: string) => ["email-send", id] as const,
  emailAudience: (id: string) => ["email-audience", id] as const,
  emailDeliveries: (id: string, status?: string) => ["email-deliveries", id, status ?? "all"] as const,
  deliverability: ["deliverability"] as const,
  mergeFields: ["merge-fields"] as const,
  insightSchema: ["insight-schema"] as const,
  insight: (q: InsightQuery) => ["insight", q] as const,
  dashboards: ["dashboards"] as const,
  dashboard: (id: string) => ["dashboard", id] as const,
  links: ["links"] as const,
  link: (id: string) => ["link", id] as const,
  publicPreferences: (token: string) => ["public-preferences", token] as const,
  publicUnsubscribe: (token: string) => ["public-unsubscribe", token] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => get<Me>("/api/auth/me"), retry: false, staleTime: 60_000 });
export const useAuthConfig = () => useQuery({ queryKey: keys.authConfig, queryFn: () => get<AuthConfig>("/api/auth/config") });
export const useConfigQuery = (enabled = true) =>
  useQuery({ queryKey: keys.config, queryFn: () => get<ConfigBundle>("/api/config"), staleTime: 5 * 60_000, enabled });

export const useHome = () => useQuery({ queryKey: keys.home, queryFn: () => get<HomeData>("/api/home") });

/** Calendar items in a range. params: from, to (ISO), kinds, people, types, orgs (comma lists), q. */
export const useCalendar = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: ["calendar", params] as const,
    queryFn: ({ signal }) => get<CalendarItem[]>(`/api/calendar${qs(params)}`, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
/** Planned calls, meetings and demos still to do, for a provider, partner or deal. */
export const usePlanned = (params: { organisationId?: string; dealId?: string }, enabled = true) =>
  useQuery({ queryKey: ["planned", params] as const, queryFn: () => get<PlannedActivity[]>(`/api/planned${qs(params)}`), enabled });
export const usePlannedItem = (id: string | null | undefined) =>
  useQuery({ queryKey: ["planned-item", id ?? ""] as const, queryFn: () => get<PlannedActivity>(`/api/planned/${id}`), enabled: !!id });
/** This person's private Google Calendar feed link (null where the hub has no public address). */
export const useCalendarFeed = (enabled = true) =>
  useQuery({ queryKey: ["calendar-feed"] as const, queryFn: () => get<{ url: string | null }>("/api/calendar/feed"), enabled, staleTime: Infinity });
/** Two-way Google Calendar sync: this person's connection, and whose plans sync. */
/** Your Gmail connection; refreshed often while the first sweep or a check runs. */
export const useMailStatus = (enabled = true) =>
  useQuery({
    queryKey: ["mail"] as const,
    queryFn: () => get<MailStatus>("/api/mail"),
    enabled,
    refetchInterval: (q) => (q.state.data?.connection?.backfilling || q.state.data?.connection?.syncing ? 5000 : false),
  });
export const useGoogleCalendar = () => useQuery({ queryKey: ["calendar-google"] as const, queryFn: () => get<GoogleCalendarStatus>("/api/calendar/google"), staleTime: 60_000 });

/** Registry list. `params` mirrors the server's organisationQuery (see server/src/services/organisations.ts). */
export const useOrganisations = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: keys.organisations(params),
    queryFn: ({ signal }) => get<Paged<OrganisationListItem>>(`/api/organisations${qs(params)}`, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useOrganisation = (id: string | undefined) =>
  useQuery({ queryKey: keys.organisation(id ?? ""), queryFn: () => get<OrganisationDetail>(`/api/organisations/${id}`), enabled: !!id });
export const useTimeline = (id: string | undefined, kinds?: string) =>
  useQuery({
    queryKey: keys.timeline(id ?? "", kinds),
    queryFn: () => get<TimelineItem[]>(`/api/organisations/${id}/timeline${qs({ kinds })}`),
    enabled: !!id,
  });

export const useContacts = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: keys.contacts(params),
    queryFn: ({ signal }) => get<Paged<ContactRegistryItem>>(`/api/contacts${qs(params)}`, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useContact = (id: string | undefined) =>
  useQuery({ queryKey: keys.contact(id ?? ""), queryFn: () => get<Contact>(`/api/contacts/${id}`), enabled: !!id });

/** Deals. params: { status?: "open,won", kind?: "new_business" | "expansion", owner?: userId } */
export const useDeals = (params: Record<string, unknown>) =>
  useQuery({ queryKey: keys.deals(params), queryFn: () => get<DealSummary[]>(`/api/deals${qs(params)}`) });
export const useDeal = (id: string | undefined) =>
  useQuery({ queryKey: keys.deal(id ?? ""), queryFn: () => get<DealDetail>(`/api/deals/${id}`), enabled: !!id });

/** Proposals made from a deal, newest first. */
export const useProposals = (dealId: string) =>
  useQuery({ queryKey: ["proposals", dealId], queryFn: () => get<ProposalSummary[]>(`/api/deals/${dealId}/proposals`) });
/** What a new proposal would show, and whether one can be made. */
export const useProposalPreview = (dealId: string, enabled: boolean) =>
  useQuery({ queryKey: ["proposal-preview", dealId], queryFn: () => get<ProposalPreview>(`/api/deals/${dealId}/proposal`), enabled });
/** Website forms and demo booking (Admin). */
export const useWebForms = () => useQuery({ queryKey: ["web-forms"], queryFn: () => get<WebForm[]>("/api/admin/forms") });
export const useWebForm = (id: string) => useQuery({ queryKey: ["web-form", id], queryFn: () => get<WebForm>(`/api/admin/forms/${id}`) });
export const useFormSubmissions = (id: string) => useQuery({ queryKey: ["form-submissions", id], queryFn: () => get<FormSubmission[]>(`/api/admin/forms/${id}/submissions`) });
export const useBookingStatus = () => useQuery({ queryKey: ["bookings"], queryFn: () => get<BookingStatus>("/api/admin/bookings") });
export const useBookingSlots = (enabled: boolean) =>
  useQuery({ queryKey: ["booking-slots"], queryFn: () => get<BookingSlot[]>("/api/admin/bookings/slots"), enabled, retry: false, staleTime: 60_000 });
export const useProposalTemplate = () =>
  useQuery({ queryKey: ["proposal-template"], queryFn: () => get<ProposalTemplateInfo | null>("/api/admin/proposal-template") });

export const useAccounts = () => useQuery({ queryKey: keys.accounts, queryFn: () => get<AccountsOverview>("/api/accounts") });

export const useSearch = (q: string) =>
  useQuery({
    queryKey: keys.search(q),
    queryFn: ({ signal }) => get<SearchResults>(`/api/search${qs({ q })}`, signal),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

export const useAudit = (params: Record<string, unknown>) =>
  useQuery({ queryKey: keys.audit(params), queryFn: () => get<AuditEntry[]>(`/api/admin/audit${qs(params)}`) });

const isBundle = (x: unknown): x is ConfigBundle => !!x && typeof x === "object" && "stages" in x && "settings" in x;

/* -------- Phase 2 -------- */

/** Partner registry. params: q, type, stage, owner, region, sort, page, pageSize (≤500). */
export const usePartners = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: keys.partners(params),
    queryFn: ({ signal }) => get<Paged<PartnerListItem>>(`/api/partners${qs(params)}`, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
export const usePartner = (id: string | undefined) =>
  useQuery({ queryKey: keys.partner(id ?? ""), queryFn: () => get<PartnerDetail>(`/api/partners/${id}`), enabled: !!id });

/** Referrals. params: status (comma list), referrer (organisation id). */
export const useReferrals = (params: Record<string, unknown> = {}) =>
  useQuery({ queryKey: keys.referrals(params), queryFn: () => get<ReferralItem[]>(`/api/referrals${qs(params)}`) });

export const useOkr = () => useQuery({ queryKey: keys.okr, queryFn: () => get<OkrOverview>("/api/okr") });
/** One key result in full: every value, and how an external one is fed (link settings, push address). */
export const useKeyResult = (id: string | null | undefined) =>
  useQuery({ queryKey: ["key-result", id ?? ""] as const, queryFn: () => get<KeyResultDetail>(`/api/key-results/${id}`), enabled: !!id });
export const useCampaigns = () => useQuery({ queryKey: keys.campaigns, queryFn: () => get<CampaignSummary[]>("/api/campaigns") });
export const useCampaign = (id: string | undefined) =>
  useQuery({ queryKey: keys.campaign(id ?? ""), queryFn: () => get<CampaignDetail>(`/api/campaigns/${id}`), enabled: !!id });
/** Asset index. params: q, tag, campaignId, kind ("pillar" | "asset"). */
export const useAssets = (params: Record<string, unknown> = {}) =>
  useQuery({ queryKey: keys.assets(params), queryFn: () => get<Asset[]>(`/api/assets${qs(params)}`), placeholderData: keepPreviousData });

export const useEmailTemplates = () => useQuery({ queryKey: keys.emailTemplates, queryFn: () => get<EmailTemplate[]>("/api/email/templates") });
export const useEmailTemplate = (id: string | undefined) =>
  useQuery({ queryKey: keys.emailTemplate(id ?? ""), queryFn: () => get<EmailTemplate>(`/api/email/templates/${id}`), enabled: !!id });
export const useEmailSends = () => useQuery({ queryKey: keys.emailSends, queryFn: () => get<EmailSendSummary[]>("/api/email/sends") });
export const useEmailSend = (id: string | undefined) =>
  useQuery({ queryKey: keys.emailSend(id ?? ""), queryFn: () => get<EmailSendDetail>(`/api/email/sends/${id}`), enabled: !!id });
export const useEmailAudience = (id: string | undefined, enabled = true) =>
  useQuery({ queryKey: keys.emailAudience(id ?? ""), queryFn: () => get<AudiencePreview>(`/api/email/sends/${id}/audience`), enabled: !!id && enabled });
/** Preview an unsaved audience (the "Email selected" dialog). */
/** Admin → Data export: every dataset, its columns and filters. */
export const useExportCatalogue = () => useQuery({ queryKey: ["export-catalogue"] as const, queryFn: () => get<ExportDatasetInfo[]>("/api/admin/exports"), staleTime: 5 * 60_000 });
/** The first rows and the total for an export as built so far. */
export const useExportPreview = (body: { dataset: string; columns: string[]; filters: ExportFilterValues }, enabled = true) =>
  useQuery({
    queryKey: ["export-preview", body] as const,
    queryFn: () => post<ExportPreview>("/api/admin/exports/preview", body),
    placeholderData: keepPreviousData,
    enabled: enabled && !!body.dataset,
  });
/** Subject access requests, open ones first by due date. */
export const useDataRequests = () => useQuery({ queryKey: ["data-requests"] as const, queryFn: () => get<DataRequest[]>("/api/admin/data-requests") });
/** Where the Prospects list came from (import sources, "Added by hand"), newest first. */
export const useOrganisationSources = () =>
  useQuery({ queryKey: ["organisation-sources"] as const, queryFn: () => get<{ source: string; count: number }[]>("/api/organisations/sources") });
/** Contacts due a retention review, longest overdue first. */
export const useRetention = () => useQuery({ queryKey: ["retention"] as const, queryFn: () => get<RetentionItem[]>("/api/admin/retention") });

export const useAudiencePreview = (body: { audience: EmailAudience; topicId?: string | null }, enabled = true) =>
  useQuery({
    queryKey: ["email-audience-preview", body] as const,
    queryFn: () => post<AudiencePreview>("/api/email/audience/preview", body),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useEmailDeliveries = (id: string | undefined, status?: string) =>
  useQuery({ queryKey: keys.emailDeliveries(id ?? "", status), queryFn: () => get<EmailDelivery[]>(`/api/email/sends/${id}/deliveries${qs({ status })}`), enabled: !!id });
export const useDeliverability = () => useQuery({ queryKey: keys.deliverability, queryFn: () => get<Deliverability>("/api/email/deliverability"), staleTime: 5 * 60_000 });
export const useMergeFields = () => useQuery({ queryKey: keys.mergeFields, queryFn: () => get<MergeField[]>("/api/email/merge-fields"), staleTime: Infinity });

export const useInsightSchema = () => useQuery({ queryKey: keys.insightSchema, queryFn: () => get<InsightDatasetSchema[]>("/api/insights/schema"), staleTime: Infinity });
/** Run an insight query (POST, cached by the query itself). */
export const useInsight = (q: InsightQuery | null) =>
  useQuery({ queryKey: keys.insight(q!), queryFn: () => api<InsightResult>("/api/insights/query", { method: "POST", body: q }), enabled: !!q, placeholderData: keepPreviousData });
export const useLinks = () => useQuery({ queryKey: keys.links, queryFn: () => get<TrackedLinksOverview>("/api/links") });
export const useLink = (id: string | null | undefined) =>
  useQuery({ queryKey: keys.link(id ?? ""), queryFn: () => get<TrackedLinkDetail>(`/api/links/${id}`), enabled: !!id });

export const useDashboards = () => useQuery({ queryKey: keys.dashboards, queryFn: () => get<Dashboard[]>("/api/dashboards") });
export const useDashboard = (id: string | undefined) =>
  useQuery({ queryKey: keys.dashboard(id ?? ""), queryFn: () => get<Dashboard>(`/api/dashboards/${id}`), enabled: !!id });

/** Public pages (no sign-in): the contact's own preference centre and unsubscribe confirmation. */
export const usePublicPreferences = (token: string | undefined) =>
  useQuery({ queryKey: keys.publicPreferences(token ?? ""), queryFn: () => get<PublicPreferences>(`/api/public/preferences/${token}`), enabled: !!token, retry: false });
export const usePublicUnsubscribe = (token: string | undefined) =>
  useQuery({
    queryKey: keys.publicUnsubscribe(token ?? ""),
    queryFn: () => get<{ topicName: string | null; scope: "topic" | "all"; email: string; preferencesToken: string | null; done: boolean }>(`/api/public/unsubscribe/${token}`),
    enabled: !!token,
    retry: false,
  });

/** Everything that might display data changed by a write. */
const DATA_KEYS: QueryKey[] = [
  ["organisations"], ["organisation"], ["timeline"], ["contacts"], ["contact"], ["deals"], ["deal"], ["accounts"], ["home"], ["search"],
  ["partners"], ["partner"], ["referrals"], ["okr"], ["campaigns"], ["campaign"], ["assets"],
  ["email-templates"], ["email-template"], ["email-sends"], ["email-send"], ["email-audience"], ["email-deliveries"], ["deliverability"],
  ["insight"], ["dashboards"], ["dashboard"], ["calendar"], ["planned"], ["planned-item"], ["calendar-google"], ["key-result"], ["export-preview"], ["data-requests"], ["retention"], ["organisation-sources"], ["mail"], ["links"], ["link"],
  ["proposals"], ["proposal-preview"], ["proposal-template"],
  ["web-forms"], ["web-form"], ["form-submissions"], ["bookings"], ["booking-slots"],
];

/**
 * A write. On success it invalidates the data views (and config, if
 * `config: true`), and shows `success` as a toast. Errors toast by default;
 * pass `quiet: true` to handle them inline (e.g. field errors in a form).
 */
export function useWrite<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: { success?: string | ((r: TResult, v: TVars) => string); config?: boolean; quiet?: boolean; onSuccess?: (r: TResult, v: TVars) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation<TResult, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: async (result, vars) => {
      // Admin list endpoints return the new ConfigBundle; others (e.g. users) don't, so refetch.
      if (opts.config && isBundle(result)) qc.setQueryData(keys.config, result);
      else if (opts.config) await qc.invalidateQueries({ queryKey: keys.config });
      await Promise.all(DATA_KEYS.map((k) => qc.invalidateQueries({ queryKey: k })));
      if (opts.success) toast.success(typeof opts.success === "function" ? opts.success(result, vars) : opts.success);
      opts.onSuccess?.(result, vars);
    },
    onError: (err) => {
      if (!opts.quiet) toast.error(err.message);
    },
  });
}
